import React from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardContent,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';

const Terms: React.FC = () => {
  const { t } = useTranslation();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/login" />
          </IonButtons>
          <IonTitle>Terms of Service</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding">
        <IonCard>
          <IonCardContent>
            <div className="prose max-w-none dark:prose-invert">
              <h1 className="text-2xl font-bold mb-4">Terms of Service</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  1. Acceptance of Terms
                </h2>
                <p className="mb-3">
                  By accessing and using this ticket trading system ("Service"),
                  you accept and agree to be bound by the terms and provision of
                  this agreement. If you do not agree to these terms, please do
                  not use this Service.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">2. Use License</h2>
                <p className="mb-3">
                  Permission is granted to temporarily access and use the
                  Service for personal, non-commercial transitory viewing only.
                  This is the grant of a license, not a transfer of title, and
                  under this license you may not:
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>modify or copy the materials</li>
                  <li>
                    use the materials for any commercial purpose or for any
                    public display
                  </li>
                  <li>
                    attempt to reverse engineer any software contained in the
                    Service
                  </li>
                  <li>
                    remove any copyright or other proprietary notations from the
                    materials
                  </li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
                <p className="mb-3">
                  When you create an account with us, you must provide
                  information that is accurate, complete, and current at all
                  times. You are responsible for safeguarding the password and
                  for any activities or actions under your account.
                </p>
                <p className="mb-3">
                  You agree to notify us immediately of any unauthorized access
                  to or use of your account or any other breach of security.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  4. Ticket Trading Rules
                </h2>
                <p className="mb-3">
                  By using our ticket trading platform, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    Only list tickets you legally own or have the right to sell
                  </li>
                  <li>Provide accurate information about all tickets listed</li>
                  <li>Complete transactions in a timely manner</li>
                  <li>Not engage in fraudulent or deceptive practices</li>
                  <li>
                    Comply with all applicable laws regarding ticket resale
                  </li>
                  <li>
                    Not use the platform for money laundering or other illegal
                    activities
                  </li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  5. Fees and Payments
                </h2>
                <p className="mb-3">
                  You agree to pay all fees and charges associated with your use
                  of the Service. All fees are non-refundable unless otherwise
                  stated. We reserve the right to change our fees at any time
                  with prior notice.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  6. Prohibited Uses
                </h2>
                <p className="mb-3">You may not use our Service:</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>
                    For any unlawful purpose or to solicit others to perform
                    unlawful acts
                  </li>
                  <li>
                    To violate any international, federal, provincial, or state
                    regulations, rules, laws, or local ordinances
                  </li>
                  <li>
                    To infringe upon or violate our intellectual property rights
                    or the intellectual property rights of others
                  </li>
                  <li>
                    To harass, abuse, insult, harm, defame, slander, disparage,
                    intimidate, or discriminate
                  </li>
                  <li>To submit false or misleading information</li>
                  <li>
                    To upload or transmit viruses or any other type of malicious
                    code
                  </li>
                  <li>
                    To interfere with or circumvent the security features of the
                    Service
                  </li>
                </ul>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  7. Content Ownership
                </h2>
                <p className="mb-3">
                  All content on this Service, including text, graphics, logos,
                  images, and software, is the property of the Service or its
                  content suppliers and is protected by international copyright
                  laws.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">8. Disclaimer</h2>
                <p className="mb-3">
                  The materials on this Service are provided "as is". We make no
                  warranties, expressed or implied, and hereby disclaim and
                  negate all other warranties, including without limitation,
                  implied warranties or conditions of merchantability, fitness
                  for a particular purpose, or non-infringement of intellectual
                  property.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">9. Limitations</h2>
                <p className="mb-3">
                  In no event shall our company or its suppliers be liable for
                  any damages (including, without limitation, damages for loss
                  of data or profit, or due to business interruption) arising
                  out of the use or inability to use the Service, even if we
                  have been notified of the possibility of such damage.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  10. Indemnification
                </h2>
                <p className="mb-3">
                  You agree to defend, indemnify, and hold harmless the Service,
                  its officers, directors, employees, and agents from and
                  against any claims, liabilities, damages, judgments, awards,
                  losses, costs, expenses, or fees arising out of or relating to
                  your violation of these Terms of Service or your use of the
                  Service.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
                <p className="mb-3">
                  We may terminate or suspend your account immediately, without
                  prior notice or liability, for any reason whatsoever,
                  including without limitation if you breach the Terms of
                  Service. Upon termination, your right to use the Service will
                  cease immediately.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  12. Dispute Resolution
                </h2>
                <p className="mb-3">
                  Any dispute arising out of or relating to these Terms of
                  Service or the Service shall be resolved through binding
                  arbitration in accordance with the rules of the jurisdiction
                  in which the Service operates.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  13. Governing Law
                </h2>
                <p className="mb-3">
                  These Terms of Service shall be governed by and construed in
                  accordance with the laws of the United Kingdom, without regard
                  to its conflict of law provisions.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  14. Changes to Terms
                </h2>
                <p className="mb-3">
                  We reserve the right to modify or replace these Terms of
                  Service at any time. If a revision is material, we will
                  provide at least 30 days notice prior to any new terms taking
                  effect.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="text-xl font-semibold mb-3">
                  15. Contact Information
                </h2>
                <p className="mb-3">
                  Questions about the Terms of Service should be sent to us at:
                </p>
                <p className="mb-3">
                  Email: contact@managetix.com
                  <br />
                  Phone: +44 (0) 20 1234 5678
                  <br />
                </p>
              </section>
            </div>
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default Terms;
